import { useState, useEffect } from 'react';
import { PIECE_THEMES, BOARD_THEMES, DUKE_BOARD_THEMES } from '../components/settings/SettingsData';

/**
 * Hook to sync settings from localStorage to CSS variables and React state.
 * Extracted from App.js to reduce component complexity.
 */
export const useSettingsSync = ({ gameMode, screen }) => {
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem('chessKingdomSettings');
    if (saved) {
      const settings = JSON.parse(saved);
      return settings.viewMode || 'topdown';
    }
    return 'topdown';
  });
  const [theme3D, setTheme3D] = useState(() => {
    const saved = localStorage.getItem('chessKingdomSettings');
    if (saved) {
      const settings = JSON.parse(saved);
      return settings.theme3D || 'Classic';
    }
    return 'Classic';
  });
  const [pieceSkinWhite, setPieceSkinWhite] = useState(() => {
    const saved = localStorage.getItem('chessKingdomSettings');
    if (saved) {
      const settings = JSON.parse(saved);
      return settings.pieceSkinWhite || settings.pieceSkin || null;
    }
    return null;
  });
  const [pieceSkinBlack, setPieceSkinBlack] = useState(() => {
    const saved = localStorage.getItem('chessKingdomSettings');
    if (saved) {
      const settings = JSON.parse(saved);
      return settings.pieceSkinBlack || settings.pieceSkin || null;
    }
    return null;
  });
  const [dukeSkinWhite, setDukeSkinWhite] = useState(() => {
    const saved = localStorage.getItem('chessKingdomSettings');
    if (saved) {
      const settings = JSON.parse(saved);
      return settings.dukeSkinWhite || settings.dukeSkin || 'default';
    }
    return 'default';
  });
  const [dukeSkinBlack, setDukeSkinBlack] = useState(() => {
    const saved = localStorage.getItem('chessKingdomSettings');
    if (saved) {
      const settings = JSON.parse(saved);
      return settings.dukeSkinBlack || settings.dukeSkin || 'default';
    }
    return 'default';
  });

  // Apply saved view mode on mount and screen changes
  useEffect(() => {
    const savedSettings = localStorage.getItem('chessKingdomSettings');
    if (savedSettings) {
      const settings = JSON.parse(savedSettings);
      const mode = settings.viewMode || 'topdown';
      const theme = settings.theme3D || 'Classic';
      setViewMode(mode);
      setTheme3D(theme);
      
      const chessBoard = document.querySelector('.chess-board-container');
      if (chessBoard) {
        chessBoard.classList.remove('view-3d', 'view-topdown', 'view-3dmodels');
        if (mode === 'topdown') {
          chessBoard.classList.add('view-topdown');
        } else if (mode === '3dmodels') {
          chessBoard.classList.add('view-3dmodels');
        } else {
          chessBoard.classList.add('view-3d');
        }
      }
    } else {
      setViewMode('topdown');
      const chessBoard = document.querySelector('.chess-board-container');
      if (chessBoard) {
        chessBoard.classList.add('view-topdown');
      }
    }
  }, [screen]);

  // Listen for settings changes (storage events + custom events)
  useEffect(() => {
    const handleStorageChange = () => {
      const savedSettings = localStorage.getItem('chessKingdomSettings');
      if (savedSettings) {
        const settings = JSON.parse(savedSettings);
        setViewMode(settings.viewMode || 'topdown');
        setTheme3D(settings.theme3D || 'Classic');
        
        const isMultiplayerWithHostTheme = gameMode === 'multiplayer' || gameMode === 'spectate';
        const hasHostSettings = settings.hostBoardTheme || settings.hostPieceSkinWhite || settings.hostPieceSkinBlack;
        
        if (!isMultiplayerWithHostTheme && !hasHostSettings) {
          setPieceSkinWhite(settings.pieceSkinWhite || settings.pieceSkin || null);
          setPieceSkinBlack(settings.pieceSkinBlack || settings.pieceSkin || null);
          setDukeSkinWhite(settings.dukeSkinWhite || settings.dukeSkin || 'default');
          setDukeSkinBlack(settings.dukeSkinBlack || settings.dukeSkin || 'default');
          
          // Apply board colors
          const dukeSkinW = settings.dukeSkinWhite || settings.dukeSkin || 'default';
          const dukeSkinB = settings.dukeSkinBlack || settings.dukeSkin || 'default';
          if (settings.autoBoardTheme !== false && (dukeSkinW !== 'default' || dukeSkinB !== 'default')) {
            const primarySkin = dukeSkinW !== 'default' ? dukeSkinW : dukeSkinB;
            const dukeBoardTheme = DUKE_BOARD_THEMES[primarySkin] || DUKE_BOARD_THEMES.default;
            document.documentElement.style.setProperty('--board-light', dukeBoardTheme.light);
            document.documentElement.style.setProperty('--board-dark', dukeBoardTheme.dark);
          } else if (settings.useCustomBoard) {
            document.documentElement.style.setProperty('--board-light', settings.customBoardLight || '#f0d9b5');
            document.documentElement.style.setProperty('--board-dark', settings.customBoardDark || '#b58863');
          } else {
            const boardTheme = BOARD_THEMES.find(t => t.id === settings.boardTheme);
            if (boardTheme) {
              document.documentElement.style.setProperty('--board-light', boardTheme.light);
              document.documentElement.style.setProperty('--board-dark', boardTheme.dark);
            }
          }
          
          // Apply piece colors
          if (settings.useCustomPieces) {
            document.documentElement.style.setProperty('--piece-white', settings.customPieceWhite || '#ffffff');
            document.documentElement.style.setProperty('--piece-black', settings.customPieceBlack || '#2d2d2d');
            document.documentElement.setAttribute('data-piece-theme', 'custom');
          } else {
            const pieceTheme = PIECE_THEMES.find(t => t.id === settings.pieceTheme);
            if (pieceTheme) {
              document.documentElement.style.setProperty('--piece-white', pieceTheme.white);
              document.documentElement.style.setProperty('--piece-black', pieceTheme.black);
              document.documentElement.setAttribute('data-piece-theme', settings.pieceTheme);
            }
          }
        }
        
        // Apply coordinate color (always apply)
        if (settings.coordinateColor) {
          document.documentElement.style.setProperty('--coordinate-color', settings.coordinateColor);
        }
        
        // Apply board size (always apply)
        if (settings.boardSize) {
          document.documentElement.style.setProperty('--board-size-scale', (settings.boardSize / 100).toString());
        }
      }
    };

    handleStorageChange();

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('settingsChanged', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('settingsChanged', handleStorageChange);
    };
  }, [gameMode]);

  return {
    viewMode, setViewMode,
    theme3D, setTheme3D,
    pieceSkinWhite, setPieceSkinWhite,
    pieceSkinBlack, setPieceSkinBlack,
    dukeSkinWhite, setDukeSkinWhite,
    dukeSkinBlack, setDukeSkinBlack
  };
};

export default useSettingsSync;
